import { NavLink } from "react-router-dom";
import {
  TreePine,
  Home,
  Users,
  Upload,
  Printer,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  GitBranch,
} from "lucide-react";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/lib/store";

// TREE_NAME is now resolved via t('app.title') inside the component

const BRANCHES = [
  { id: "all", label: "All Branches" },
  { id: "kalnins", label: "Kalnins Line" },
  { id: "berzins", label: "Berzins Line" },
  { id: "ozols", label: "Ozols Line" },
];

export default function Sidebar() {
  const { t } = useTranslation();
  const { sidebarCollapsed, toggleSidebarCollapsed, selectedBranch, setSelectedBranch } =
    useUIStore();

  const currentBranch = BRANCHES.find((b) => b.id === selectedBranch) || BRANCHES[0];

  const navItems = [
    { to: "/", icon: Home, label: t('nav.dashboard'), end: true },
    { to: "/people", icon: Users, label: t('nav.people') },
    { to: "/tree/tree-1", icon: TreePine, label: t('nav.treeView') },
    { to: "/import", icon: Upload, label: t('nav.importExport') },
    { to: "/print", icon: Printer, label: t('nav.print') },
    { to: "/tree/tree-1/settings", icon: Settings, label: t('nav.settings') },
    { to: "/tree/tree-1/groups", icon: Shield, label: t('nav.groups') },
  ];

  return (
    <aside
      className={`relative flex flex-col border-r border-warmgray-200 bg-white transition-all duration-200 ${
        sidebarCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Logo / Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-warmgray-200 px-4">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-bark-600">
          <TreePine className="h-5 w-5 text-white" />
        </div>
        {!sidebarCollapsed && (
          <span className="font-serif text-lg font-semibold text-bark-800">
            {t('app.title')}
          </span>
        )}
      </div>

      {/* Branch Navigator */}
      {!sidebarCollapsed && (
        <div className="border-b border-warmgray-200 px-3 py-3">
          <p className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wider text-warmgray-400">
            {t('app.title')}
          </p>
          <Menu as="div" className="relative">
            <MenuButton className="flex w-full items-center justify-between rounded-lg border border-warmgray-200 px-3 py-2 text-left text-sm hover:bg-warmgray-50">
              <span className="flex items-center gap-2 truncate font-medium text-warmgray-700">
                <GitBranch className="h-3.5 w-3.5 text-warmgray-400" />
                {currentBranch.label}
              </span>
              <ChevronDown className="h-4 w-4 text-warmgray-400" />
            </MenuButton>
            <MenuItems className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-warmgray-200 bg-white py-1 shadow-lg">
              {BRANCHES.map((branch) => (
                <MenuItem key={branch.id}>
                  {({ focus }) => (
                    <button
                      onClick={() => setSelectedBranch(branch.id)}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                        focus
                          ? "bg-bark-50 text-bark-700"
                          : "text-warmgray-700"
                      } ${
                        branch.id === currentBranch.id
                          ? "font-medium text-bark-700"
                          : ""
                      }`}
                    >
                      <GitBranch className="h-3.5 w-3.5 text-warmgray-400" />
                      {branch.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        </div>
      )}

      {/* Navigation Links */}
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-1">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-bark-50 text-bark-700"
                      : "text-warmgray-600 hover:bg-warmgray-100 hover:text-warmgray-800"
                  } ${sidebarCollapsed ? "justify-center" : ""}`
                }
                title={sidebarCollapsed ? label : undefined}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Collapse Toggle */}
      <div className="border-t border-warmgray-200 p-3">
        <button
          onClick={toggleSidebarCollapsed}
          className="flex w-full items-center justify-center rounded-lg p-2 text-warmgray-400 hover:bg-warmgray-100 hover:text-warmgray-600"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>
    </aside>
  );
}
